import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

type RevenueCardProps = {
  title: string;
  value: number;
  subtitle?: string;
  isLoading?: boolean;
  formatValue?: (value: number) => string;
  previousValue?: number;
};

const RevenueCard = ({
  title,
  value,
  subtitle,
  isLoading = false,
  formatValue = (val) => val.toLocaleString(),
  previousValue,
}: RevenueCardProps) => {
  const calculateChange = () => {
    if (previousValue === undefined || previousValue === 0) {
      return null;
    }

    const change = value - previousValue;
    const percentChange = (change / previousValue) * 100;

    return {
      change,
      percentChange,
      isIncrease: change > 0,
      isDecrease: change < 0,
      isNoChange: change === 0,
    };
  };

  const changeData = calculateChange();

  if (isLoading) {
    return (
      <Card className="h-full min-h-[140px]">
        <CardContent className="h-full flex items-center justify-center py-6">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardContent className="flex flex-col gap-4 py-6">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </p>

        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-[clamp(1.375rem,1.6vw,2.25rem)] font-bold text-gray-900 leading-tight tabular-nums break-words">
            {formatValue(value)}
          </p>

          {changeData && (
            <div className="flex items-center gap-1.5">
              {changeData.isIncrease ? (
                <TrendingUp className="w-4 h-4 text-green-500" />
              ) : changeData.isDecrease ? (
                <TrendingDown className="w-4 h-4 text-red-500" />
              ) : (
                <Minus className="w-4 h-4 text-gray-500" />
              )}
              <p
                className={cn(
                  "text-sm font-medium",
                  changeData.isIncrease && "text-green-600",
                  changeData.isDecrease && "text-red-600",
                  changeData.isNoChange && "text-gray-600"
                )}
              >
                {changeData.isIncrease && "+"}
                {changeData.percentChange.toFixed(1)}% vs mes anterior
              </p>
            </div>
          )}
        </div>

        {subtitle && (
          <p className="text-sm text-gray-500 mt-auto">
            {subtitle}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default RevenueCard;
